import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { RootState } from "../../app/store";
import { ItemAdminType, ItemCardType, ItemType } from "../../types/types";
import { API_URL, CATALOG_FILE_URL, catalogUrl } from "../../config";

type PresignedHeaders = { "Content-Type": string };

export type PresignedUrlResponse = {
  bucket: string;
  key: string;
  uploadUrl: string;
  getUrl: string;
  expiresInSeconds: number;
  headers: PresignedHeaders;
};

export type GetPresignedUrlArgs = { id: string; contentType?: string; _ts?: number };

export type UpdateItemArgs = {
  id: string;
  patch: Record<string, unknown>;
};

export const itemsApi = createApi({
  reducerPath: "items",
  baseQuery: fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) headers.set("Authorization", `Bearer ${token}`);
      headers.set("Content-Type", "application/json");
      return headers;
    },
  }),
  tagTypes: ["Item"],
  endpoints: (builder) => ({
    // The admin grid catalog. Slim like the storefront's, but for a different reason: the grid needs
    // *inactive* rows above all — they are exactly the ones an admin has to curate — so this is
    // deliberately unfiltered, and narrowed only by projection (EB-521).
    //
    // `view=admin` drops `description`, `specs` and `images`, which are the fields a curated product
    // gains. Without it the grid's payload grows as the 1,837 synced products get curated; with it,
    // it stays flat. See docs/catalog-modernization/README.md §6.6.
    getItems: builder.query<ItemAdminType[], void>({
      query: () => ({
        url: "items?view=admin",
        method: "GET",
      }),
      providesTags: ["Item"],
    }),
    // The storefront catalog. Kept separate from `getItems` rather than parameterising it, so the
    // admin grid and `RelatedProductsEditor` keep their full rows — including the inactive ones the
    // admin has to curate. `active=true` is what makes a catalog sync storefront-invisible: synced
    // rows land `is_active: false`, and without this parameter they would go straight to customers
    // (EB-503, docs/catalog-modernization/README.md §5.2).
    //
    // It ships only *after* EB-499's activation backfill has run in the target environment —
    // filtering first would hide every row the backfill had not yet activated
    // (§8.3: backfill → filter → sync, in that order).
    getItemCards: builder.query<ItemCardType[], void>({
      // The storefront catalog, in preference order (EB-507, EB-508):
      //
      //   1. `catalog.json` on the CDN — a file the sync and every admin write rebuild. No API
      //      Gateway, no DynamoDB `Scan`, and the response is compressed at the edge.
      //   2. `GET /items?view=card&active=true`, through the CDN when one is configured.
      //   3. The same request straight to API Gateway, when it is not.
      //
      // The fallback is not defensive decoration: it is what makes the file optional. Before the
      // first publish, and any time the object is missing, the storefront works exactly as it did
      // — so this ships without having to be sequenced against a sync run.
      //
      // A `queryFn` rather than a `query` because the choice depends on the *outcome* of the first
      // attempt, which `query` has no way to express.
      async queryFn(_arg, _api, _extra, baseQuery) {
        if (CATALOG_FILE_URL) {
          try {
            const response = await fetch(CATALOG_FILE_URL);
            if (response.ok) return { data: (await response.json()) as ItemCardType[] };
          } catch {
            // Network error, CORS, a 404 before the first publish — all the same answer: ask the
            // API. Deliberately not reported as an error; the catalog still loads.
          }
        }

        const result = await baseQuery({
          url: catalogUrl("items?view=card&active=true"),
          method: "GET",
        });

        return result.error ? { error: result.error } : { data: result.data as ItemCardType[] };
      },
      providesTags: ["Item"],
    }),
    getItemById: builder.query<ItemType, string>({
      query: (id) => `items/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Item", id }],
    }),
    createItem: builder.mutation<unknown, ItemType>({
      query: (body) => ({
        url: "items",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Item"],
    }),
    updateItem: builder.mutation<unknown, UpdateItemArgs>({
      query: ({ id, patch }) => ({
        url: `items/${id}`,
        method: "PUT",
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Item", id }, "Item"],
    }),
    deleteItem: builder.mutation<{ id: string; deleted: boolean }, string>({
      query: (id) => ({ url: `items/${id}`, method: "DELETE" }),
      invalidatesTags: (_result, _error, id) => [{ type: "Item", id }, "Item"],
    }),
    getPreSignedUrl: builder.query<PresignedUrlResponse, GetPresignedUrlArgs>({
      query: ({ id, contentType }) => {
        const params = new URLSearchParams({ id });
        if (contentType) params.set("contentType", contentType);
        return { url: `getPresignedUrl?${params.toString()}`, method: "GET" };
      },
    }),
  }),
});

export const {
  useGetItemsQuery,
  useGetItemCardsQuery,
  useGetItemByIdQuery,
  useCreateItemMutation,
  useUpdateItemMutation,
  useDeleteItemMutation,
  useLazyGetPreSignedUrlQuery,
} = itemsApi;

export default itemsApi;
